import { useEffect, useMemo } from "react"

import {
	AEMLeafNode,
	AEMPageType,
	AEMSectionNode
} from "@AEM/models/AEMPage.model"
import { useAEMNavData } from "@AEM/hooks/useAEMNavData"
import { useAEMAllowedToolsContext } from "@context/AEMAllowedToolsContext"

function extractToolCodes(
	acc: Set<string>,
	nodes: (AEMSectionNode | AEMLeafNode)[] = []
) {
	for (const node of nodes) {
		if ("toolCode" in node && node.toolCode) {
			acc.add(node.toolCode)
		}

		if ("children" in node && node.children?.items) {
			extractToolCodes(acc, node.children.items)
		}
	}
}

export function useAllowedToolsFromAEM() {
	const { data: globalNav } = useAEMNavData(AEMPageType.NAVIGATION_GLOBAL)
	const { data: sidebarNav } = useAEMNavData(AEMPageType.NAVIGATION_SIDEBAR)

	const { tools, setTools } = useAEMAllowedToolsContext()

	// extract tool codes (saved in memory)
	const extractedTools = useMemo(() => {
		const toolSet = new Set<string>()

		if (globalNav?.[0]?.children?.items) {
			extractToolCodes(toolSet, globalNav[0].children.items)
		}

		if (sidebarNav?.[0]?.children?.items) {
			extractToolCodes(toolSet, sidebarNav[0].children.items)
		}

		return Array.from(toolSet)
	}, [globalNav, sidebarNav])

	// push to context once ready
	useEffect(() => {
		if (extractedTools.length > 0) {
			setTools((prev: string[]) =>
				JSON.stringify(prev) === JSON.stringify(extractedTools)
					? prev
					: extractedTools
			)
		}
	}, [extractedTools, setTools])

	return {
		tools,
		isLoading: (!globalNav || !sidebarNav) && extractedTools.length === 0,
		isReady: extractedTools.length > 0
	}
}
